import type { User } from "firebase/auth";
import type { AccountDetails } from "@iluvatar/global/src/utilities/account-setup";
import type { TDecodedToken } from "@/contexts/authentication/authentication.types";
import requestService from "./request.service";

export type AccountSession = {
  token: TDecodedToken;
  accountDetails: AccountDetails;
};

const pendingSessions = new WeakMap<User, Promise<AccountSession>>();

/** Check persisted profile details on sign-in, reloads, and navigation. */
export async function getAccountSession(
  user: User,
  forceRefresh = false,
): Promise<AccountSession> {
  const existing = pendingSessions.get(user);
  if (existing) {
    return existing;
  }
  const session = (async () => {
    const token = (await user.getIdTokenResult(forceRefresh)) as TDecodedToken;
    const [, response] = await requestService.request<{
      data: AccountDetails & { setupRequired: boolean };
    }>("/api/account/session", { method: "post" });
    const { setupRequired, ...accountDetails } = response.data;
    // Refresh changed setup claims without causing repeated token events.
    const authToken =
      token.claims.perm && token.claims.setupRequired === setupRequired
        ? token
        : ((await user.getIdTokenResult(true)) as TDecodedToken);
    return { token: authToken, accountDetails };
  })().finally(() => pendingSessions.delete(user));
  pendingSessions.set(user, session);
  return session;
}
